import {
  clearCompletedMemberApplicationOperation,
  getOrCreateMemberApplicationOperation,
  MemberApplicationOperationStorage,
} from "../../Members/memberApplicationOperation";
import {
  CMP_EVENT_HOSTED_CHECKOUT_ACTION_BLOCKED_CODE,
  CMP_EVENT_INVENTORY_CAPACITY_UNAVAILABLE_CODE,
} from "./eventSalesApolloErrorPolicy";

export type EventSalesOperationAction =
  | "UPDATE_REGISTRATION_ATTENDEES"
  | "UPDATE_SPONSORSHIP_ATTENDEES"
  | "CREATE_REGISTRATION"
  | "START_REGISTRATION_CARD_CHECKOUT"
  | "RECORD_REGISTRATION_MANUAL_PAYMENT"
  | "CANCEL_REGISTRATION"
  | "APPROVE_SPONSORSHIP"
  | "START_SPONSORSHIP_CARD_CHECKOUT"
  | "RECORD_SPONSORSHIP_MANUAL_PAYMENT"
  | "CANCEL_SPONSORSHIP"
  | "SAVE_INVOICE_DRAFT";

export type EventSalesOperationInput<MutationInput extends object> = {
  action: EventSalesOperationAction;
  chamberId: string;
  createOperationId?: () => string;
  entityId: string;
  eventId: string;
  fingerprintRequest?: (request: unknown) => Promise<string>;
  input: MutationInput & { operationId?: unknown };
  storage?: MemberApplicationOperationStorage;
};

export type PreparedEventSalesOperation<MutationInput extends object> = {
  input: Omit<MutationInput, "operationId"> & { operationId: string };
  operationId: string;
  storageKey: string;
};

export type RunEventSalesOperationInput<
  MutationInput extends object,
  MutationResult,
> = EventSalesOperationInput<MutationInput> & {
  execute: (
    input: Omit<MutationInput, "operationId"> & { operationId: string }
  ) => Promise<MutationResult>;
  isAuthoritativelyCompleted: (result: MutationResult) => boolean;
};

export type EventSalesSingleFlightGuard = {
  current: boolean;
};

export type RunEventSalesSingleFlightInput<Result> = {
  execute: () => Promise<Result>;
  inFlightGuard: EventSalesSingleFlightGuard;
};

export const getEventSalesOperationStorageKey = ({
  action,
  chamberId,
  entityId,
  eventId,
}: {
  action: EventSalesOperationAction;
  chamberId: string;
  entityId: string;
  eventId: string;
}) =>
  `cmp-event-sales-operation:v1:${action}:${encodeURIComponent(
    chamberId
  )}:${encodeURIComponent(eventId)}:${encodeURIComponent(entityId)}`;

// GraphQL codes the backend uses to refuse a request before it saves anything.
// INTERNAL_SERVER_ERROR, network failures and missing codes stay unknown.
const definitiveEventSalesRefusalCodes = new Set<string>([
  "BAD_USER_INPUT",
  "FORBIDDEN",
  CMP_EVENT_HOSTED_CHECKOUT_ACTION_BLOCKED_CODE,
  CMP_EVENT_INVENTORY_CAPACITY_UNAVAILABLE_CODE,
]);

type GraphQLRefusalLike = {
  graphQLErrors?: ReadonlyArray<{ extensions?: { code?: unknown } | null }>;
  networkError?: unknown;
};

/**
 * True only for a GraphQL response in which every error is a business or
 * input refusal. Such a response proves the mutation saved nothing.
 */
export const isDefinitiveEventSalesRefusal = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }

  const { graphQLErrors, networkError } = error as GraphQLRefusalLike;
  if (networkError || !Array.isArray(graphQLErrors) || !graphQLErrors.length) {
    return false;
  }

  return graphQLErrors.every((graphQLError) => {
    const code = graphQLError?.extensions?.code;
    return typeof code === "string" && definitiveEventSalesRefusalCodes.has(code);
  });
};

/**
 * Acquires a synchronous, component-owned guard before calling any async
 * preparation work. Apollo's loading state is updated after a render, so it
 * cannot prevent a second click from entering request fingerprinting and
 * minting a competing operation identity in the same browser turn.
 */
export const runEventSalesSingleFlight = async <Result>({
  execute,
  inFlightGuard,
}: RunEventSalesSingleFlightInput<Result>): Promise<Result | undefined> => {
  if (inFlightGuard.current) {
    return undefined;
  }

  inFlightGuard.current = true;
  try {
    return await execute();
  } finally {
    inFlightGuard.current = false;
  }
};

/**
 * Binds an opaque, persisted UUID to the exact operation-free mutation input.
 * Caller-supplied operation IDs are deliberately removed before hashing and
 * dispatch so replay authority always comes from this coordinator.
 */
export const prepareEventSalesOperation = async <MutationInput extends object>({
  action,
  chamberId,
  createOperationId,
  entityId,
  eventId,
  fingerprintRequest,
  input,
  storage,
}: EventSalesOperationInput<MutationInput>): Promise<
  PreparedEventSalesOperation<MutationInput>
> => {
  const operationFreeInput = { ...input } as Record<string, unknown>;
  delete operationFreeInput.operationId;

  const storageKey = getEventSalesOperationStorageKey({
    action,
    chamberId,
    entityId,
    eventId,
  });
  const operation = await getOrCreateMemberApplicationOperation({
    ...(createOperationId ? { createOperationId } : {}),
    ...(fingerprintRequest ? { fingerprintRequest } : {}),
    request: {
      action,
      chamberId,
      entityId,
      eventId,
      input: operationFreeInput,
    },
    ...(storage ? { storage } : {}),
    storageKey,
  });

  return {
    input: {
      ...operationFreeInput,
      operationId: operation.operationId,
    } as Omit<MutationInput, "operationId"> & { operationId: string },
    operationId: operation.operationId,
    storageKey,
  };
};

/** Clears only the exact operation acknowledged by an authoritative response. */
export const completeEventSalesOperation = <MutationInput extends object>({
  operation,
  storage,
}: {
  operation: PreparedEventSalesOperation<MutationInput>;
  storage?: MemberApplicationOperationStorage;
}): boolean =>
  clearCompletedMemberApplicationOperation({
    operationId: operation.operationId,
    ...(storage ? { storage } : {}),
    storageKey: operation.storageKey,
  });

/**
 * Unknown, rejected, and network-error outcomes retain the operation so the
 * next identical attempt replays the same backend mutation identity.
 */
export const runEventSalesOperation = async <
  MutationInput extends object,
  MutationResult,
>({
  execute,
  isAuthoritativelyCompleted,
  ...prepareInput
}: RunEventSalesOperationInput<
  MutationInput,
  MutationResult
>): Promise<MutationResult> => {
  const prepared = await prepareEventSalesOperation(prepareInput);
  const result = await execute(prepared.input);

  if (isAuthoritativelyCompleted(result)) {
    completeEventSalesOperation({
      operation: prepared,
      ...(prepareInput.storage ? { storage: prepareInput.storage } : {}),
    });
  }

  return result;
};
